import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Textarea,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';
import { fetchLocalidadesCatalogo, LocalidadOption } from '../../../utils/localidadesCatalogo';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const FormularioPrograma: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    nombre: '',
    tipo: '',
    ejercicioFiscal: '',
    presupuesto: '',
    reglasOperacion: '',
    requisitos: '',
    fechaInicio: '',
    fechaFin: '',
    metaProgramada: '',
    estatus: 'activo',
    localidades: '',
  });
  const [guardando, setGuardando] = useState(false);
  const [catalogoLocalidades, setCatalogoLocalidades] = useState<LocalidadOption[]>([]);

  useEffect(() => {
    fetchLocalidadesCatalogo().then(setCatalogoLocalidades).catch(() => setCatalogoLocalidades([]));
  }, []);

  useEffect(() => {
    if (data) {
      setForm({
        nombre: data.nombre || '',
        tipo: data.tipo || '',
        ejercicioFiscal: data.ejercicioFiscal || '',
        presupuesto: data.presupuesto || '',
        reglasOperacion: data.reglasOperacion || '',
        requisitos: data.requisitos || '',
        fechaInicio: data.fechaInicio ? data.fechaInicio.split('T')[0] : '',
        fechaFin: data.fechaFin ? data.fechaFin.split('T')[0] : '',
        metaProgramada: data.metaProgramada || '',
        estatus: data.estatus || 'activo',
        localidades: Array.isArray(data.localidades) ? data.localidades.map((l: any) => l.localidadId).join(', ') : '',
      });
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const toArray = (value: string) =>
    value
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      .map((v) => Number(v));

  const handleSubmit = async () => {
    if (!form.nombre || !form.tipo || !form.ejercicioFiscal || !form.metaProgramada) return;
    setGuardando(true);
    try {
      const payload = {
        ...form,
        localidades: form.localidades ? toArray(form.localidades) : [],
      };
      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/desarrollo-social/programas/${data.id}`, payload);
      } else {
        await axiosInstance.post('/desarrollo-social/programas', payload);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        <FormControl isRequired>
          <FormLabel>Nombre</FormLabel>
          <Input value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Tipo</FormLabel>
          <Select value={form.tipo} onChange={(e) => handleChange('tipo', e.target.value)} isDisabled={disabled}>
            <option value="">Selecciona</option>
            <option value="municipal">Municipal</option>
            <option value="estatal">Estatal</option>
            <option value="federal">Federal</option>
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Ejercicio fiscal</FormLabel>
          <Input type="number" value={form.ejercicioFiscal} onChange={(e) => handleChange('ejercicioFiscal', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Presupuesto</FormLabel>
          <Input type="number" value={form.presupuesto} onChange={(e) => handleChange('presupuesto', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Reglas de operacion</FormLabel>
          <Textarea value={form.reglasOperacion} onChange={(e) => handleChange('reglasOperacion', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Requisitos</FormLabel>
          <Textarea value={form.requisitos} onChange={(e) => handleChange('requisitos', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Fecha inicio</FormLabel>
          <Input type="date" value={form.fechaInicio} onChange={(e) => handleChange('fechaInicio', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Fecha fin</FormLabel>
          <Input type="date" value={form.fechaFin} onChange={(e) => handleChange('fechaFin', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Meta programada</FormLabel>
          <Input type="number" value={form.metaProgramada} onChange={(e) => handleChange('metaProgramada', e.target.value)} isDisabled={disabled} />
        </FormControl>
        <FormControl>
          <FormLabel>Estatus</FormLabel>
          <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
            <option value="activo">Activo</option>
            <option value="inactivo">Inactivo</option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Localidades</FormLabel>
          <Select
            multiple
            h="140px"
            value={form.localidades ? String(form.localidades).split(',').filter(Boolean) : []}
            onChange={(e) => {
              const values = Array.from(e.target.selectedOptions).map((opt) => opt.value);
              handleChange('localidades', values.join(', '));
            }}
            isDisabled={disabled}
          >
            {catalogoLocalidades.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.label}</option>
            ))}
          </Select>
        </FormControl>
      </SimpleGrid>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">Guardar</Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioPrograma;

